import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { AvatarModule } from 'primeng/avatar';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DialogModule } from 'primeng/dialog';
import { Subject, takeUntil } from 'rxjs';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { ChatService } from '../../../../services/chat/chat.service';
import { ChatMessage, ChatBotResponse } from '../../../../models/chat/chat-message.interface';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../../../states/user/user-selector';
import { UserStateModel } from '../../../../states/user/user-state-model';

@Component({
  selector: 'app-chat-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    ScrollPanelModule,
    AvatarModule,
    IconFieldModule,
    InputIconModule,
    DialogModule
  ],
  templateUrl: './chat-dialog.component.html',
  styleUrls: ['./chat-dialog.component.scss']
})
export class ChatDialogComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;

  visible = false;
  messages: ChatMessage[] = [];
  newMessage = '';
  isLoading = false;
  user: UserStateModel | undefined;
  private destroy$ = new Subject<void>();

  // Inject services using the inject function
  private chatService = inject(ChatService);
  private store = inject(Store);
  private sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.getUser();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  async showDialog(initialMessage?: string): Promise<void> {
    this.visible = true;
    await this.initializeChat();
    
    if (initialMessage && initialMessage.trim()) {
      this.newMessage = initialMessage;
      // Small delay to ensure the dialog is fully open before sending
      setTimeout(() => {
        this.sendMessage();
      }, 100);
    }
    
    setTimeout(() => {
      this.focusInput();
    }, 300);
  }

  hideDialog(): void {
    this.visible = false;
    this.messages = [];
    this.newMessage = '';
    this.isLoading = false;
  }

  private getUser(): void {
    this.store.select(UserSelectors.getUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.user = response;
        },
        error: (error) => {
          console.warn('Error fetching user:', error);
        }
      });
  }

  private async initializeChat(): Promise<void> {
    await this.addBotMessage('Hi, I\'m your assistant! How can I help you today?');
  }

  async sendMessage(): Promise<void> {
    if (!this.newMessage.trim() || this.isLoading) {
      return;
    }

    const userMessage = this.newMessage.trim();
    this.addUserMessage(userMessage);
    this.newMessage = '';
    this.isLoading = true;

    const chatRequest = {
      question: userMessage,
      userId: this.user?.id
    };

    this.chatService.sendMessage(chatRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (response) => {
          this.isLoading = false;
          if (response && response.isSuccess) {
            await this.addBotMessage(response.answer);
          } else {
            await this.addBotMessage('Sorry, I encountered an error. Please try again.');
          }
        },
        error: async (error) => {
          this.isLoading = false;
          console.error('Error sending message:', error);
          await this.addBotMessage('Sorry, I encountered an error. Please try again.');
        }
      });
  }

  private addUserMessage(content: string): void {
    const message: ChatMessage = {
      id: this.generateId(),
      content,
      timestamp: new Date(),
      isUser: true
    };
    this.messages.push(message);
  }

  private async addBotMessage(content: string): Promise<void> {
    const convertedContent = await this.convertMarkdownToHtml(content);
    const message: ChatMessage = {
      id: this.generateId(),
      content: convertedContent,
      timestamp: new Date(),
      isUser: false
    };
    this.messages.push(message);
  }

  private async convertMarkdownToHtml(markdown: string): Promise<SafeHtml> {
    try {
      // Convert markdown to HTML (marked returns a Promise in newer versions)
      const htmlContent = await marked(markdown);

      // Sanitize the HTML content using DOMPurify
      const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'table', 'thead',
          'tbody', 'tr', 'th', 'td', 'hr', 'div', 'span'
        ],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'class', 'style'],
        ALLOW_DATA_ATTR: false
      });

      return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      // Fallback to plain text if markdown conversion fails
      return this.sanitizer.bypassSecurityTrustHtml(markdown);
    }
  }

  async getMessageContent(content: string | SafeHtml): Promise<SafeHtml> {
    if (typeof content === 'string') {
      return await this.convertMarkdownToHtml(content);
    }
    return content;
  }

  private generateId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  private scrollToBottom(): void {
    try {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.warn('Error scrolling to bottom:', err);
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  focusInput(): void {
    setTimeout(() => {
      if (this.messageInput) {
        this.messageInput.nativeElement.focus();
      }
    }, 100);
  }
}
